import React from 'react';
import { motion } from 'framer-motion';
import { Edit2, Trash2, Tag, Package } from 'lucide-react';
import { Product } from '../types';

interface ProductCardProps {
  product: Product;
  onEdit: (product: Product) => void;
  onDelete: (id: string) => void;
}

export function ProductCard({ product, onEdit, onDelete }: ProductCardProps) {
  const isPromotionValid = (promotion: Product['promotion']) => {
    if (!promotion?.is_active) return false;
    
    const now = new Date().getTime();
    const startDate = promotion.start_date ? new Date(promotion.start_date).getTime() : null;
    const endDate = promotion.end_date ? new Date(promotion.end_date).getTime() : null;
    
    if (endDate && now > endDate) return false;
    if (startDate && now < startDate) return false;
    
    return true;
  };

  const hasPromotion = product.promotion && isPromotionValid(product.promotion);
  const originalPrice = product.price;
  let finalPrice = originalPrice;
  
  if (hasPromotion && product.promotion) {
    if (product.promotion.discount_type === 'percentage') {
      finalPrice = originalPrice * (1 - product.promotion.discount_value / 100);
    } else {
      finalPrice = originalPrice - (product.promotion?.discount_value || 0);
    }
  }

  const isLowStock = product.stock_quantity <= product.min_stock_quantity;
  const stockPercentage = product.min_stock_quantity > 0
    ? (product.stock_quantity / product.min_stock_quantity) * 100
    : 100;

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="bg-white rounded-2xl overflow-hidden shadow-sm border border-gray-100 hover:shadow-md transition-all"
    >
      {hasPromotion && (
        <div className="bg-gradient-to-r from-green-500 to-emerald-500 px-3 py-1.5 flex items-center justify-center gap-1.5">
          <Tag className="h-3.5 w-3.5 text-white" />
          <span className="text-xs font-semibold text-white">
            {product.promotion.discount_type === 'percentage'
              ? `${product.promotion.discount_value}% הנחה`
              : `הנחה של ${product.promotion.discount_value}₪`}
          </span>
        </div>
      )}

      {isLowStock && (
        <div className="bg-gradient-to-r from-red-500 to-orange-500 px-3 py-1.5 flex items-center justify-center gap-1.5">
          <Package className="h-3.5 w-3.5 text-white" />
          <span className="text-xs font-semibold text-white">מלאי נמוך - נותרו {product.stock_quantity} יחידות</span>
        </div>
      )}

      <div className="p-4">
        <div className="flex gap-3">
          {product.image_url ? (
            <div className="w-20 h-20 rounded-xl overflow-hidden flex-shrink-0 shadow-sm">
              <img
                src={product.image_url}
                alt={product.name_he}
                className="w-full h-full object-cover"
              />
            </div>
          ) : (
            <div className="w-20 h-20 rounded-xl bg-gradient-to-br from-emerald-500 to-emerald-600 flex items-center justify-center flex-shrink-0 shadow-sm">
              <Package className="h-8 w-8 text-white" />
            </div>
          )}

          <div className="flex-1 min-w-0">
            <h3 className="font-bold text-gray-900 text-base mb-2">{product.name_he}</h3>

            <div className="flex items-center gap-2 mb-2 flex-wrap">
              {product.sku && (
                <div className="px-2 py-1 bg-gray-100 rounded-lg">
                  <span className="text-xs font-medium text-gray-700">מק"ט: {product.sku}</span>
                </div>
              )}
              <div className="flex items-center gap-1 px-2 py-1 bg-emerald-50 rounded-lg">
                <Package className="h-3.5 w-3.5 text-emerald-600" />
                <span className="text-xs font-medium text-emerald-700">{product.stock_quantity} במלאי</span>
              </div>
            </div>

            <div className="flex items-center justify-between">
              <div className="flex items-baseline gap-2">
                {hasPromotion && (
                  <span className="text-sm text-gray-400 line-through">₪{originalPrice}</span>
                )}
                <span className="text-xl font-bold text-gray-900">₪{finalPrice}</span>
              </div>

              <div className="flex gap-1">
                <button
                  onClick={() => onEdit(product)}
                  className="p-2 text-gray-400 active:text-blue-600 transition-colors rounded-lg active:bg-blue-50"
                >
                  <Edit2 className="h-4.5 w-4.5" />
                </button>
                <button
                  onClick={() => onDelete(product.id)}
                  className="p-2 text-gray-400 active:text-red-600 transition-colors rounded-lg active:bg-red-50"
                >
                  <Trash2 className="h-4.5 w-4.5" />
                </button>
              </div>
            </div>
          </div>
        </div>

        {product.description && (
          <p className="mt-3 text-sm text-gray-600 leading-relaxed line-clamp-2">{product.description}</p>
        )}
      </div>
    </motion.div>
  );
}